import React from "react";

import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { Grid, useMediaQuery } from "@material-ui/core";
import { useSelector } from "react-redux";

import { FaIcon } from "components/AgGridButtonIcon/agGridButtonIcon";
import { SubtleBadge } from "components/Badge";
import { Warning } from "components/ErrorMessage";
import { CollapseLabel, FormCollapse } from "components/Form";
import { DeploymentSelectField } from "components/Form/Fields";
import {
  AutoSizeTextArea,
  Input,
  SelectField,
  Switch,
  withNamespace,
} from "components/Form/FormikControls";
import { BranchSelect } from "components/Form/FormikControls/BranchSelect";
import { Alternative } from "components/Business/BusinessForm/lib";

import { sectionHasErrors } from "lib/form";
import { pluralize } from "lib/pluralize";

import {
  getBusinessById,
  getBusinesses,
  getSelectableBranches,
} from "selectors";
import { selectBusinessLabelsAggridData } from "selectors/businessLabel";

import { useTheme } from "hooks";
import toast from "lib/toast";
import {
  findUnknownLabelNames,
  formatSkippedLabelsMessage,
} from "constants/sideLoadedBusinesses";
import { mapBusinessValuesToInsuranceType } from "lib/insuranceRates";

import { AlternateFieldTextComponent, getAlternateColor } from "./lib";
import { BusinessFormValues } from "./types";

import { SideLoadedBusinessAlertController } from "components/Business/SideLoadedBusinesses/SideLoadedBusinessAlertController";
import { SideLoadedSelectValues } from "components/Business/SideLoadedBusinesses/CompareModal/SideLoadedFieldCompareModal";
import { getIn, setIn, useFormikContext } from "formik";
import { useHasSideLoadedBusinessPermission } from "hooks/useHasPermission";

interface GeneralCollapseProps {
  isOpen: boolean;
  onToggle: () => void;
  namespace?: string;
  businessId?: string | null;
  openSideLoadedBusinessModal?: boolean;
  sideLoadedBusinessId?: string;
}

type GeneralCollapseFormValues = BusinessFormValues & {
  branchName?: string;
};

export const GeneralCollapse = ({
  isOpen,
  onToggle,
  namespace: ns = "",
  businessId,
  openSideLoadedBusinessModal,
  sideLoadedBusinessId,
}: GeneralCollapseProps): React.JSX.Element => {
  const businesses: StateModelById<Business> = useSelector(getBusinesses);
  const businessLabels = useSelector(selectBusinessLabelsAggridData);
  const business: Business | undefined = useSelector(
    getBusinessById(businessId),
  );
  const branches = useSelector(getSelectableBranches);
  const formikProps = useFormikContext<GeneralCollapseFormValues>();
  const { values } = formikProps;
  const { breakpoints } = useTheme();
  const {
    branchName,
    isTopBuyerCattle,
    isTopBuyerSheep,
    isVendor,
    shortCode,
    shortCodeSaleyard,
  } = values;

  const duplicateShortCodes = Object.values(businesses).filter(
    b => b.id !== businessId && b.shortCode === shortCode,
  );

  const duplicateSyRefs = Object.values(businesses).filter(
    b => b.id !== businessId && b.shortCodeSaleyard === shortCodeSaleyard,
  );

  const isMobile = useMediaQuery(`(max-width: ${breakpoints[1]}px)`);
  function topBuyerCheck(): React.ReactNode {
    if (isTopBuyerSheep || isTopBuyerCattle) {
      return (
        <>
          {isMobile ? "TB" : "Top Buyer"}
          <FaIcon icon={faCheck} color="success" />
        </>
      );
    } else {
      return "";
    }
  }
  const hasErrorBadge = sectionHasErrors(formikProps, [
    "name",
    "shortCode",
    "shortCodeSaleyard",
    "branchId",
  ]);

  const header = (
    <Grid container>
      <SubtleBadge hasErrorBadge={hasErrorBadge}>General</SubtleBadge>
      <CollapseLabel> {topBuyerCheck()} </CollapseLabel>
      <CollapseLabel>{shortCode}</CollapseLabel>
      <CollapseLabel>{branchName}</CollapseLabel>
    </Grid>
  );

  const hasSideLoadBusinessPermission = useHasSideLoadedBusinessPermission();

  const nameGridSizingProps = hasSideLoadBusinessPermission
    ? ({
        xs: 12,
        sm: 8,
        md: 10,
      } as const)
    : ({ xs: 12 } as const);

  const alternatives = (business?.alternatives || []) as Alternative[];

  const getAltColor = (field: string): string =>
    getAlternateColor(alternatives, values, field, null, undefined);

  const getAltTooltip = (field: string): React.JSX.Element =>
    AlternateFieldTextComponent(alternatives, field);

  const handlePopulateSideloadBusiness = (
    sideLoadedValues: SideLoadedSelectValues,
  ): void => {
    const {
      recordId,
      fields,
      contacts,
      properties,
      vendorCommissions,
      relationships,
      notes,
      labelNames,
    } = sideLoadedValues;

    // Side-loaded PICs arrive uppercased; a business may hold the same PIC
    // in another casing.
    const normalizePic = (pic: string | undefined): string =>
      (pic || "").toUpperCase();
    const sideLoadedPics = new Set(properties.map(p => normalizePic(p.PIC)));

    // Restored in place rather than added again, so the row keeps its id.
    const updatedProperties = values.properties.map(property =>
      !property.isShown && sideLoadedPics.has(normalizePic(property.PIC))
        ? { ...property, isShown: true }
        : property,
    );

    const shownPics = new Set(
      updatedProperties
        .filter(property => property.isShown)
        .map(property => normalizePic(property.PIC)),
    );
    const newProperties = properties.filter(
      p => !shownPics.has(normalizePic(p.PIC)),
    );
    const existingVendorCommissions = values.vendorCommissionBands || [];
    const existingRelationships = values.relationships || [];
    const existingNotes = values.notes || "";
    // Names the deployment already has resolve to their label. The rest are
    // skipped - a label has to exist before a side-loaded record can apply it.
    const labelIdByName = new Map(
      businessLabels.map(label => [label.name.toLowerCase(), label.id]),
    );
    const acceptedLabelIds = labelNames
      .map(name => labelIdByName.get(name.toLowerCase()))
      .filter(Boolean);
    // Shared with the compare row's warning, so what it says will be skipped
    // is what actually gets skipped.
    const unknownLabelNames = findUnknownLabelNames(
      labelNames,
      businessLabels.map(label => label.name),
    );
    const skippedLabelsMessage = formatSkippedLabelsMessage(unknownLabelNames);
    if (skippedLabelsMessage) {
      toast.warning(skippedLabelsMessage);
    }
    const newSideLoadedDataIds = [
      ...(values.sideLoadedDataIds || []),
      recordId,
    ];

    // Merge or update vendor commission bands
    const updatedVendorCommissions = [...existingVendorCommissions];
    vendorCommissions.forEach(newBand => {
      const existingIndex = updatedVendorCommissions.findIndex(
        b =>
          (b.saleType ?? null) === (newBand.saleType ?? null) &&
          (b.speciesId ?? null) === (newBand.speciesId ?? null) &&
          (b.saleSubTypeId ?? null) === (newBand.saleSubTypeId ?? null),
      );
      if (existingIndex >= 0) {
        updatedVendorCommissions[existingIndex] = {
          ...updatedVendorCommissions[existingIndex],
          ...newBand,
          // Preserve existing persistent database ID so the backend updates instead of inserting
          id: updatedVendorCommissions[existingIndex].id ?? newBand.id,
        };
      } else {
        updatedVendorCommissions.push(newBand);
      }
    });

    // Merge or update relationships
    const updatedRelationships = [...existingRelationships];
    relationships.forEach(newRel => {
      const existingIndex = updatedRelationships.findIndex(
        r =>
          r.relatedToId === newRel.relatedToId &&
          r.relationType === newRel.relationType &&
          (r.saleType ?? null) === (newRel.saleType ?? null) &&
          (r.speciesId ?? null) === (newRel.speciesId ?? null) &&
          (r.saleSubTypeId ?? null) === (newRel.saleSubTypeId ?? null),
      );
      if (existingIndex >= 0) {
        updatedRelationships[existingIndex] = {
          ...updatedRelationships[existingIndex],
          ...newRel,
          // Preserve existing persistent database ID so the backend updates instead of inserting
          id: updatedRelationships[existingIndex].id ?? newRel.id,
        };
      } else {
        updatedRelationships.push(newRel);
      }
    });

    let updatedValues: GeneralCollapseFormValues = {
      ...values,
      // A little blunt with email recipients - we don't have dedupeability.
      emailRecipients: [...values.emailRecipients, ...contacts],
      properties: [...updatedProperties, ...newProperties],
      sideLoadedDataIds: newSideLoadedDataIds,
      vendorCommissionBands: updatedVendorCommissions,
      relationships: updatedRelationships,
      notes: [existingNotes, notes].filter(Boolean).join("\n"),
      labelIds: [...new Set([...(values.labelIds || []), ...acceptedLabelIds])],
    };

    // Handle namespaced values (eg addressDetails.locality, etc)
    Object.entries(fields).forEach(([field, value]) => {
      updatedValues = setIn(updatedValues, field, value);
    });

    // Only update the transitInsuranceApplicationDefault if either of the transit fields in fields.
    if (
      "hasTransitInsuranceForSales" in fields ||
      "hasTransitInsuranceForPurchases" in fields
    ) {
      updatedValues.transitInsuranceApplicationDefault =
        mapBusinessValuesToInsuranceType(updatedValues) || "";
    }

    const touchedFields = Object.keys(fields)
      .concat([
        "properties",
        "emailRecipients",
        "sideLoadedDataIds",
        "relationships",
        "vendorCommissionBands",
        "notes",
        "labelIds",
      ])
      .reduce<Record<string, boolean>>((acc, field) => {
        acc[field] = true;
        // Add address, bankingDetails, etc as touched.
        const parts = field.split(".");
        if (parts.length > 1) {
          parts.forEach(part => {
            acc[part] = true;
          });
        }
        return acc;
      }, {});

    // avoid validation race, don't validate here.
    formikProps.setValues(updatedValues, false);
    formikProps.setTouched(touchedFields, false);
  };

  return (
    <FormCollapse
      isOpen={isOpen}
      onToggle={onToggle}
      hasErrorBadge={hasErrorBadge}
      header={header}
      dataTour="generalCollapse"
    >
      <Grid item {...nameGridSizingProps}>
        <Input
          label="Name"
          name={withNamespace(ns, "name")}
          required
          altTooltip={getAltTooltip("name")}
          altColor={getAltColor("name")}
          disableAutoComplete
        />
      </Grid>
      {hasSideLoadBusinessPermission && (
        <Grid
          item
          container
          justifyContent="flex-end"
          alignItems="flex-end"
          xs={12}
          sm={4}
          md={2}
        >
          <SideLoadedBusinessAlertController
            searchValues={getIn(values, withNamespace(ns, "name")) || ""}
            ns={ns}
            handlePopulateSideloadBusiness={handlePopulateSideloadBusiness}
            businessId={businessId}
            initialOpenState={openSideLoadedBusinessModal}
            initialFieldCompareRecordId={sideLoadedBusinessId}
          />
        </Grid>
      )}
      <Grid item xs={12} sm={12}>
        <Input
          label="Public Display Name"
          name={withNamespace(ns, "publicDisplayName")}
          altTooltip={getAltTooltip("publicDisplayName")}
          altColor={getAltColor("publicDisplayName")}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          label="Agent Reference"
          name={withNamespace(ns, "shortCode")}
          altTooltip={getAltTooltip("shortCode")}
          altColor={getAltColor("shortCode")}
          maxLength={16}
        />
        {shortCode && duplicateShortCodes.length > 0 && (
          <Warning>
            {duplicateShortCodes.length} other{" "}
            {pluralize("business", duplicateShortCodes.length)} with the same
            reference found
          </Warning>
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          label="Saleyard Reference"
          name={withNamespace(ns, "shortCodeSaleyard")}
          altTooltip={getAltTooltip("shortCodeSaleyard")}
          altColor={getAltColor("shortCodeSaleyard")}
          maxLength={16}
        />
        {shortCodeSaleyard && duplicateSyRefs.length > 0 && (
          <Warning>
            {duplicateSyRefs.length} other{" "}
            {pluralize("business", duplicateSyRefs.length)} with the same
            reference found
          </Warning>
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          label="List2Sell Agent Ref"
          name={withNamespace(ns, "slug")}
          tooltip="Agent reference code in list2sell listing"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          label="AuctionsPlus Short Code"
          name={withNamespace(ns, "shortCodeAuctionsPlus")}
          tooltip=" The business' unique Auctions+ User ID"
          altTooltip={getAltTooltip("shortCodeAuctionsPlus")}
          altColor={getAltColor("shortCodeAuctionsPlus")}
        />
      </Grid>
      {branches.length > 0 && (
        <Grid item xs={isVendor ? 6 : 12}>
          <BranchSelect label="Branch" name={withNamespace(ns, "branchId")} />
        </Grid>
      )}
      {isVendor && (
        <Grid item xs={6}>
          <DeploymentSelectField
            label="Default Consigning Branch"
            name={withNamespace(ns, "defaultConsigningDeploymentId")}
            onChangeExtra={null}
            restrictToOwnDeployments={false}
            retrictToSaleDeployments={false}
          />
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <AutoSizeTextArea
          label="Notes"
          name={withNamespace(ns, "notes")}
          disabled={false}
          tooltip="Add any additional notes here"
          rows={7}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          label="Brands"
          name={withNamespace(ns, "brands")}
          tooltip="This brand will be automatically populated into the NVD Mapping Screen.  Any changes are shared across the system."
        />
      </Grid>
      <Grid item container xs={12}>
        <Grid item xs={4} md>
          <Switch
            label="Active"
            name={withNamespace(ns, "isActive")}
            altTooltip={getAltTooltip("isActive")}
            altColor={getAltColor("isActive")}
          />
        </Grid>
        <Grid item xs={4} md>
          <Switch label="Vendor" name={withNamespace(ns, "isVendor")} />
        </Grid>
        <Grid item xs={4} md>
          <Switch
            label="Transporter"
            name={withNamespace(ns, "isTransporter")}
          />
        </Grid>
        <Grid item xs={4} md>
          <Switch label="Buyer" name={withNamespace(ns, "isBuyer")} />
        </Grid>
        <Grid item xs={4} md>
          <Switch label="Published" name={withNamespace(ns, "isPublished")} />
        </Grid>
        <Grid item xs={4} md>
          <Switch
            label="Meat Processor"
            name={withNamespace(ns, "isMeatProcessorCommercialBuyer")}
          />
        </Grid>
        <Grid item xs={6} md>
          <Switch
            label="Top Buyer Sheep"
            name={withNamespace(ns, "isTopBuyerSheep")}
            altTooltip={getAltTooltip("isTopBuyerSheep")}
            altColor={getAltColor("isTopBuyerSheep")}
          />
        </Grid>
        <Grid item xs={6} md>
          <Switch
            label="Top Buyer Cattle"
            name={withNamespace(ns, "isTopBuyerCattle")}
            altTooltip={getAltTooltip("isTopBuyerCattle")}
            altColor={getAltColor("isTopBuyerCattle")}
          />
        </Grid>
      </Grid>
      {businessLabels.length > 0 && (
        <Grid item xs={12}>
          <SelectField
            label="Labels"
            name={withNamespace(ns, "labelIds")}
            options={businessLabels.map(label => ({
              value: label.id,
              label: label.name,
            }))}
            isMulti
            isClearable
            menuPortalTarget={document.body}
            tooltip="Select labels to categorise this business."
          />
        </Grid>
      )}
    </FormCollapse>
  );
};
